import { useEffect, useCallback } from "react";
import { X, ChevronLeft, ChevronRight, Download } from "lucide-react";

/**
 * Full-bleed image viewer for a scope's gallery. Opens on the clicked frame and
 * steps through that section only — arrow keys, on-screen arrows, Esc to close.
 *
 * `items` is the ordered image list, `index` the open one (null = closed).
 */
export default function MediaLightbox({ items = [], index, onClose, onIndexChange, mediaUrl }) {
  const open = index !== null && index !== undefined && items[index];

  const step = useCallback(
    (delta) => {
      if (!items.length) return;
      onIndexChange((index + delta + items.length) % items.length);
    },
    [index, items, onIndexChange]
  );

  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      else if (e.key === "ArrowRight") step(1);
      else if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    // Don't let the page scroll behind the viewer.
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = previousOverflow;
    };
  }, [open, onClose, step]);

  if (!open) return null;

  const item = items[index];
  const url = mediaUrl(item);

  return (
    <div
      /* pointer-events-auto because this opens over a scope dialog, and a modal
         Radix dialog turns pointer events off everywhere but its own content. */
      className="fixed inset-0 z-[100] pointer-events-auto flex flex-col animate-soft-fade"
      style={{ backgroundColor: "rgba(0,0,0,0.94)" }}
      onClick={onClose}
      data-testid="portfolio-lightbox"
    >
      {/* Top rail — counter, download, close */}
      <div className="flex items-center justify-between px-5 py-4 shrink-0" onClick={(e) => e.stopPropagation()}>
        <span className="micro-label text-white/60 tabular">
          {index + 1} / {items.length}
        </span>
        <div className="flex items-center gap-1">
          <a
            href={url}
            download={item.file_name || item.title || "image"}
            className="p-2.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-smooth"
            title="Download"
            onClick={(e) => e.stopPropagation()}
          >
            <Download className="w-4 h-4" />
          </a>
          <button
            onClick={onClose}
            className="p-2.5 rounded-full text-white/70 hover:text-white hover:bg-white/10 transition-smooth"
            title="Close"
            data-testid="lightbox-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
      </div>

      <div className="flex-1 min-h-0 flex items-center justify-center px-2 sm:px-14">
        {items.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); step(-1); }}
            className="absolute left-2 sm:left-5 p-3 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-smooth"
            title="Previous"
            data-testid="lightbox-prev"
          >
            <ChevronLeft className="w-7 h-7" />
          </button>
        )}

        <img
          src={url}
          alt={item.caption || item.title || ""}
          className="max-h-full max-w-full object-contain"
          onClick={(e) => e.stopPropagation()}
        />

        {items.length > 1 && (
          <button
            onClick={(e) => { e.stopPropagation(); step(1); }}
            className="absolute right-2 sm:right-5 p-3 rounded-full text-white/60 hover:text-white hover:bg-white/10 transition-smooth"
            title="Next"
            data-testid="lightbox-next"
          >
            <ChevronRight className="w-7 h-7" />
          </button>
        )}
      </div>

      <div className="px-5 py-5 shrink-0 text-center" onClick={(e) => e.stopPropagation()}>
        {(item.caption || item.title) && (
          <p className="text-sm text-white/70 font-light">{item.caption || item.title}</p>
        )}
      </div>
    </div>
  );
}
